import type { Patient, Reference } from '@medplum/fhirtypes'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { omitBy } from 'lodash-es'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

export type TaskComposerDefaultValues = Partial<
  Pick<
    StandardTask,
    | 'title'
    | 'description'
    | 'state'
    | 'priority'
    | 'ownerPractitioner'
    | 'dueOn'
    | 'participant'
    | 'focus'
    | 'basedOn'
    | 'code'
    | 'labels'
  >
>

interface TaskComposerStore {
  isInDesk: boolean
  setIsInDesk: (isInDesk: boolean) => void
  currentParticipant: Reference<Patient> | undefined
  setCurrentParticipant: (participant: Reference<Patient> | undefined) => void
  open: boolean
  defaultValues: TaskComposerDefaultValues
  setOpen: (open: boolean) => void
  setDefaultValues: (
    defaultValues:
      | TaskComposerDefaultValues
      | ((prev: TaskComposerDefaultValues) => TaskComposerDefaultValues)
  ) => void
  onTaskCreated: ((task: StandardTask) => void) | undefined
  setOnTaskCreated: (
    callback: ((task: StandardTask) => void) | undefined
  ) => void
  onClose: (() => void) | undefined
  setOnClose: (callback: (() => void) | undefined) => void
}

export const useTaskComposerStore = create<TaskComposerStore>()(
  devtools(
    persist(
      (set, get) => ({
        isInDesk: false,
        setIsInDesk: (isInDesk: boolean) => set({ isInDesk }),
        currentParticipant: undefined,
        setCurrentParticipant: (participant: Reference<Patient> | undefined) =>
          set({ currentParticipant: participant }),
        open: false,
        defaultValues: {} as TaskComposerDefaultValues,
        setOpen: (open: boolean) => set({ open }),
        setDefaultValues: (
          defaultValues:
            | TaskComposerDefaultValues
            | ((prev: TaskComposerDefaultValues) => TaskComposerDefaultValues)
        ) =>
          set({
            defaultValues:
              typeof defaultValues === 'function'
                ? defaultValues(get().defaultValues)
                : defaultValues,
          }),
        onTaskCreated: undefined,
        setOnTaskCreated: (callback) => set({ onTaskCreated: callback }),
        onClose: undefined,
        setOnClose: (callback) => set({ onClose: callback }),
      }),
      {
        name: 'task-composer',
        partialize: (state) => ({
          ...state,
          defaultValues: omitNullValues(state.defaultValues),
          onTaskCreated: undefined,
          onClose: undefined,
        }),
      }
    )
  )
)

function omitNullValues(
  rest: TaskComposerDefaultValues
): TaskComposerDefaultValues {
  return omitBy(rest, (value) => !value)
}
